import {
  ArrowRightLeft,
  CheckCircle,
  FileText,
  Sparkles,
  TrendingUp,
  Zap,
} from 'lucide-react';

// Quranic verses about trade and business
const quranicVerse = {
  verse: 'وَأَحَلَّ اللَّهُ الْبَيْعَ وَحَرَّمَ الرِّبَا',
  reference: 'سورة البقرة - الآية 275',
};

export function InitialScreen() {
  return (
    <div className="relative min-h-[400px] w-full overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Primary gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800"></div>

        {/* Floating orbs */}
        <div className="absolute -right-16 -top-16 h-32 w-32 animate-pulse rounded-full bg-gradient-to-br from-blue-400/20 to-purple-600/20 blur-2xl dark:from-blue-600/10 dark:to-purple-800/10"></div>
        <div className="absolute -bottom-16 -left-16 h-32 w-32 animate-pulse rounded-full bg-gradient-to-tr from-indigo-400/20 to-pink-600/20 blur-2xl dark:from-indigo-600/10 dark:to-pink-800/10"></div>
        <div className="dark:from-emerald-600/8 dark:to-teal-800/8 absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full bg-gradient-to-br from-emerald-400/15 to-teal-600/15 blur-xl"></div>

        {/* Animated grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage: `
              linear-gradient(90deg, #6366f1 1px, transparent 1px),
              linear-gradient(180deg, #6366f1 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Main Container */}
      <div className="relative mx-auto max-w-5xl p-4">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200/50 bg-white/80 shadow-xl backdrop-blur-xl dark:border-slate-700/50 dark:bg-slate-900/80">
          {/* Shimmer effect */}
          <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent dark:via-white/5"></div>

          {/* Border glow */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-indigo-500/10 p-[1px] dark:from-blue-400/5 dark:via-purple-400/5 dark:to-indigo-400/5">
            <div className="h-full w-full rounded-2xl bg-white/90 dark:bg-slate-900/90"></div>
          </div>

          <div className="relative p-6">
            {/* Header Section */}
            <div className="mb-6 text-center">
              <div className="mb-4 flex justify-center">
                <div className="relative">
                  {/* Main icon glow */}
                  <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 opacity-30 blur-lg"></div>

                  {/* Main icon container */}
                  <div className="relative flex h-16 w-16 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 shadow-xl">
                    <ArrowRightLeft className="h-8 w-8 text-white" />
                  </div>

                  {/* Floating accent icons */}
                  <div className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 shadow-md">
                    <TrendingUp className="h-3 w-3 text-white" />
                  </div>
                  <div className="absolute -bottom-2 -left-2 flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 shadow-md">
                    <FileText className="h-3 w-3 text-white" />
                  </div>
                  <div className="absolute -top-1 left-1/2 flex h-5 w-5 -translate-x-1/2 items-center justify-center rounded-full bg-pink-500 shadow-md">
                    <Sparkles className="h-2.5 w-2.5 text-white" />
                  </div>
                </div>
              </div>

              <h1 className="mb-3 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-800 bg-clip-text text-3xl font-bold tracking-tight text-transparent dark:from-slate-100 dark:via-blue-200 dark:to-purple-200 md:text-4xl">
                تحويل فاتورة بيع الي طلبية وارد
              </h1>

              <p className="mx-auto max-w-xl text-base text-slate-600 dark:text-slate-400">
                قم بتحويل فواتير البيع إلى طلبات وارد بسهولة وسرعة. نظام متطور
                لتبسيط عمليات التحويل التجارية.
              </p>
            </div>

            {/* Features Grid */}
            <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
              <div className="group relative overflow-hidden rounded-xl border border-slate-200/50 bg-gradient-to-br from-blue-50 to-indigo-50 p-4 transition-all duration-300 hover:shadow-md dark:border-slate-700/50 dark:from-slate-800/50 dark:to-slate-700/50">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                  <Zap className="h-5 w-5 text-white" />
                </div>
                <h3 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                  تحويل سريع
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  تحويل فوري مع الحفاظ على جميع البيانات
                </p>
              </div>

              <div className="group relative overflow-hidden rounded-xl border border-slate-200/50 bg-gradient-to-br from-emerald-50 to-teal-50 p-4 transition-all duration-300 hover:shadow-md dark:border-slate-700/50 dark:from-slate-800/50 dark:to-slate-700/50">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <h3 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                  دقة عالية
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  ضمان دقة البيانات والتحويلات
                </p>
              </div>

              <div className="group relative overflow-hidden rounded-xl border border-slate-200/50 bg-gradient-to-br from-purple-50 to-pink-50 p-4 transition-all duration-300 hover:shadow-md dark:border-slate-700/50 dark:from-slate-800/50 dark:to-slate-700/50">
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-purple-500 to-pink-600 shadow-md">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <h3 className="mb-1 text-base font-semibold text-slate-900 dark:text-slate-100">
                  تتبع متقدم
                </h3>
                <p className="text-xs text-slate-600 dark:text-slate-400">
                  مراقبة شاملة لجميع العمليات
                </p>
              </div>
            </div>

            {/* Call to Action Section */}
            <div className="mb-6 rounded-xl border border-slate-200/50 bg-gradient-to-r from-blue-50/50 via-indigo-50/50 to-purple-50/50 p-4 dark:border-slate-700/50 dark:from-slate-800/50 dark:via-slate-700/50 dark:to-slate-800/50">
              <div className="flex flex-col items-center gap-3 text-center md:flex-row md:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
                    <FileText className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                      ابدأ التحويل الآن
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      أدخل رقم الفاتورة للبدء في عملية التحويل
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 px-3 py-1.5 text-white shadow-md">
                  <Sparkles className="h-3 w-3" />
                  <span className="text-xs font-medium">جاهز للتحويل</span>
                </div>
              </div>
            </div>

            {/* Quranic Verse Section */}
            <div className="relative overflow-hidden rounded-xl border border-slate-200/50 bg-gradient-to-br from-amber-50/50 via-orange-50/50 to-yellow-50/50 p-4 dark:border-slate-700/50 dark:from-amber-950/30 dark:via-orange-950/30 dark:to-yellow-950/30">
              {/* Background pattern */}
              <div
                className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
                style={{
                  backgroundImage: `
                    radial-gradient(#f59e0b 1px, transparent 1px)
                  `,
                  backgroundSize: '20px 20px',
                }}
              />

              <div className="relative text-center">
                <div className="mb-3 flex justify-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-500 to-orange-600 shadow-md">
                    <span className="text-sm font-bold text-white">☪</span>
                  </div>
                </div>

                <div className="mb-2 rounded-lg border border-amber-200/50 bg-white/70 p-3 backdrop-blur-sm dark:border-amber-800/50 dark:bg-slate-800/70">
                  <p className="font-arabic text-base leading-relaxed text-amber-800 dark:text-amber-200">
                    {quranicVerse.verse}
                  </p>
                </div>

                <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
                  {quranicVerse.reference}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
